import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import SockJS from 'sockjs-client/dist/sockjs.min.js';

import Stomp from 'webstomp-client';
import { f7 } from 'framework7-vue';
import _ from 'lodash';
import i18n from '@/js/i18n.js';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '../pinia-stores/auth.js';
import { useMessageStore } from '../pinia-stores/message.js';
// import { useCardStore } from '../pinia-stores/card.js';
// import { usePaymentStore } from '../pinia-stores/payment.js';
import { useAccountStore } from '../pinia-stores/account.js';
import { useNotificationStore } from '../pinia-stores/notification.js';
import { usePostStore } from '../pinia-stores/post.js';
import { useKeycloakStore } from '../pinia-stores/keycloak.js';

export function useWebSocket(options = {}) {
  const { f7router } = options;
  const instance = getCurrentInstance();
  const t = i18n.global.t;

  const authStore = useAuthStore();
  const messageStore = useMessageStore();
  // const cardStore = useCardStore();
  // const paymentStore = usePaymentStore();
  const accountStore = useAccountStore();
  const notificationStore = useNotificationStore();
  const postStore = usePostStore();
  const keycloakStore = useKeycloakStore();

  const { profile, authenticated, csrfToken, documentsUrl } = storeToRefs(authStore);
  const { chatUser, group, groupList, counterPartUser, messageSocketClient, msgCltConnected } = storeToRefs(messageStore);

  const socketClient = ref('');

  function connectMessageSocket() {
    const socketUrl = `${import.meta.env.VITE_APP_URL}ws`;
    const socket = new SockJS(socketUrl);
    socket.binaryType = 'blob';
    const stompClient = Stomp.over(socket);
    socketClient.value = stompClient;
    messageStore.msgConnectSocketClient(stompClient);
    const headers = {
      [csrfToken.value.headerName]: csrfToken.value.token,
      Authorization: `Bearer ${keycloakStore.token}`,
    };
    const onConnect = () => {
      messageStore.msgConnectSocketed(true);
      connectMessageClient();
      notificationsServiceConnect();
    };
    const onError = (error) => {
      console.error(error);
      messageStore.msgConnectSocketed(false);
      if (keycloakStore.authenticated && !msgCltConnected.value) {
        // optionally retry
      }
    };
    if (keycloakStore.authenticated) {
      stompClient.connect(headers, onConnect, onError);
    } else {
      messageStore.msgConnectSocketed(false);
    }
  }

  function connectMessageClient() {
    if (!authenticated.value) return;
    // `chatUser` starts as `{}`, so a truthiness check never fires here and the
    // sender id stays undefined; ensureChatUser() de-duplicates the request.
    messageStore.ensureChatUser();
    if (!Array.isArray(groupList.value) || !groupList.value.length) {
      messageStore.getUserGroups();
    }
    setTimeout(() => {
      connectMsgdService();
      messageSocketClient.value.subscribe('/user/queue/onlineStatus', (data) => {
        const user = JSON.parse(data.body);
        messageStore.setCounterPartUSerOnlineStatus(user);
      });
      [
        ['/user/queue/errors', (data) => console.error('WebSocket error:', data.body)],
        ['/topic/outgoing', (data) => console.log(data.body)],
      ].forEach(([topic, handler]) => messageSocketClient.value.subscribe(topic, handler));
    }, 1000);
  }

  function connectMsgdService() {
    const subscribeToGroup = (grp, isSubGroup = false) => {
      const groupId = grp.id;
      messageSocketClient.value.subscribe(`/topic/message.group.${groupId}`, (data) => {
        const newMessage = JSON.parse(data.body);
        if (isSubGroup) { addSubGroupMessage(newMessage); }
        else { addGroupMessage(newMessage); }
      });
      messageSocketClient.value.subscribe(`/topic/reaction.group.${groupId}`, (data) => {
        const reactionData = JSON.parse(data.body);
        addReactionToMessage(reactionData, isSubGroup);
      });
      messageSocketClient.value.subscribe(`/topic/message.typing.${groupId}`, (data) => {
        const typingData = JSON.parse(data.body);
        handleTyping(typingData, isSubGroup);
      });
    };
    for (const myChatGroup of groupList.value) {
      if (myChatGroup.groupType !== 'PERSONAL') {
        subscribeToGroup(myChatGroup);
        if (myChatGroup.subGroupList && myChatGroup.subGroupList.length > 0) {
          for (const myChatSubGroup of myChatGroup.subGroupList) {
            subscribeToGroup(myChatSubGroup, true);
          }
        }
      }
    }
    messageSocketClient.value.subscribe('/user/queue/message.in', (data) => {
      const newMessage = JSON.parse(data.body);
      addUserMessage(newMessage);
    });
    messageSocketClient.value.subscribe('/user/queue/reaction.in', (data) => {
      const newMessage = JSON.parse(data.body);
      addUserMessage(newMessage);
    });
    messageSocketClient.value.subscribe('/user/queue/message.typing', (data) => {
      const typingData = JSON.parse(data.body);
      handleTyping(typingData, false, true);
    });
    messageSocketClient.value.subscribe('/user/queue/onlineStatus', (data) => {
      const myChatUser = JSON.parse(data.body);
      messageStore.setCounterPartUSerOnlineStatus(myChatUser);
    });
  }

  function handleTyping(typingData, isSubGroup = false, isPersonal = false) {
    // Same identity problem as the bubbles: one helper, every id we know.
    const isNotSelf = !messageStore.isOwnMessage(typingData);
    let isCurrentGroup = false;
    if (isPersonal) {
      isCurrentGroup = typingData.senderId === counterPartUser.value?.userId && typingData.groupId === group.value?.id;
    } else {
      isCurrentGroup = typingData.receiverId === group.value?.id;
    }
    if (typingData.typing) {
      if (isCurrentGroup && (isPersonal || isNotSelf)) {
        messageStore.setTypingMessage(typingData);
      } else if (!isCurrentGroup && isNotSelf) {
        const typingTrue = f7.$(`.isTypingTrue${typingData.groupId}`);
        const typingFalse = f7.$(`.isTypingFalse${typingData.groupId}`);
        typingFalse.css('display', 'none');
        typingTrue.css('display', 'block').css('color', 'var(--f7-theme-color)')
          .text(t('tamtammessage.isTyping', { isTyping: `${typingData.name}` }));
      }
    } else {
      messageStore.setTypingMessage('');
      const typingTrue = f7.$(`.isTypingTrue${typingData.groupId}`);
      const typingFalse = f7.$(`.isTypingFalse${typingData.groupId}`);
      typingTrue.html('').css('display', 'none');
      typingFalse.css('display', 'block');
    }
  }

  function addReactionToMessage(reactionData, isSubGroup) {
    const isCurrentGroup = reactionData.receiverId === group.value?.id;
    if (isCurrentGroup) {
      messageStore.updateGroupMessage(reactionData);
    }
    let groupIndex = -1;
    let subGroupIndex = -1;
    if (isSubGroup) {
      groupIndex = groupList.value.findIndex((g) => g.id === reactionData.groupId);
      if (groupIndex !== -1) {
        subGroupIndex = groupList.value[groupIndex].subGroupList.findIndex((sg) => sg.id === reactionData.receiverId);
      }
    } else {
      groupIndex = groupList.value.findIndex((g) => g.id === reactionData.receiverId);
    }
    if (groupIndex !== -1) {
      let targetGroup = groupList.value[groupIndex];
      let messageListData = targetGroup.groupMessageList;
      if (isSubGroup && subGroupIndex !== -1) {
        targetGroup = targetGroup.subGroupList[subGroupIndex];
        messageListData = targetGroup.subGroupMessageList;
      }
      targetGroup.lastMessageAt = reactionData.sentAt;
      const msgIndex = messageListData.findIndex((m) => m.id === reactionData.id);
      if (msgIndex !== -1) {
        messageListData.splice(msgIndex, 1, reactionData);
      }
      messageStore.setUnReadMsgCount({ id: reactionData.receiverId || reactionData.groupId, count: 1 });
      f7.$(`.newMessageTime${reactionData.receiverId}`).html(instance?.proxy?.$filtersToDate?.msgDate(reactionData.sentAt, 'time'));
      if (isSubGroup) {
        groupList.value[groupIndex].subGroupList = _.orderBy(groupList.value[groupIndex].subGroupList, ['lastMessageAt'], ['asc']);
      }
      messageStore.updateGroupList(groupList.value);
    }
  }

  function addGroupMessage(newMessage) {
    const isCurrentGroup = newMessage.receiverId === group.value?.id;
    const isOwn = messageStore.isOwnMessage(newMessage);
    const groupIndex = groupList.value.findIndex((x) => x.id === newMessage.receiverId);
    if (isCurrentGroup) {
      messageStore.updateGroupMessage(newMessage);
    } else {
      if (groupIndex !== -1) {
        groupList.value[groupIndex].groupMessageList.unshift(newMessage);
        // A forwarded message comes back on this topic too; counting it as
        // unread would put an unread badge on a conversation the user just
        // wrote to.
        if (!isOwn) {
          messageStore.setUnReadMsgCount({ id: newMessage.groupId || newMessage.receiverId, count: 1 });
        }
        f7.$(`.newMessageTime${newMessage.receiverId}`).html(instance?.proxy?.$filtersToDate?.msgDate(newMessage.sentAt, 'time'));
        groupList.value[groupIndex].lastMessageAt = newMessage.sentAt;
        messageStore.updateGroupList(groupList.value);
      }
    }
  }

  function addSubGroupMessage(newMessage) {
    const isCurrentGroup = newMessage.receiverId === group.value?.id;
    const isOwn = messageStore.isOwnMessage(newMessage);
    if (isCurrentGroup) {
      messageStore.updateGroupMessage(newMessage);
    } else {
      const groupIndex = groupList.value.findIndex((g) => g.id === newMessage.groupId);
      if (groupIndex !== -1) {
        const subGroupIndex = groupList.value[groupIndex].subGroupList.findIndex((sg) => sg.id === newMessage.receiverId);
        if (subGroupIndex !== -1) {
          groupList.value[groupIndex].subGroupList[subGroupIndex].subGroupMessageList.unshift(newMessage);
          if (!isOwn) {
            messageStore.setUnReadMsgCount({ id: newMessage.receiverId || newMessage.groupId, count: 1 });
          }
          f7.$(`.newMessageTime${newMessage.receiverId}`).html(instance?.proxy?.$filtersToDate?.msgDate(newMessage.sentAt, 'time'));
          groupList.value[groupIndex].subGroupList[subGroupIndex].lastMessageAt = newMessage.sentAt;
        }
        groupList.value[groupIndex].lastMessageAt = newMessage.sentAt;
        groupList.value[groupIndex].subGroupList = _.orderBy(groupList.value[groupIndex].subGroupList, ['lastMessageAt'], ['asc']);
        messageStore.updateGroupList(groupList.value);
      }
    }
  }

  function addUserMessage(newMessage) {
    const isInvitation = newMessage.msgOrigin === 'invitation';
    const isCurrentGroup = group.value?.id === newMessage.groupId;
    const isOwn = messageStore.isOwnMessage(newMessage);
    if (isInvitation) {
      messageStore.setInfoMsg(newMessage);
    } else if (isCurrentGroup) {
      messageStore.updateGroupMessage(newMessage);
    } else {
      const groupIndex = groupList.value.findIndex((x) => x.id === newMessage.groupId);
      if (groupIndex >= 0) {
        groupList.value[groupIndex].groupMessageList.unshift(newMessage);
        if (!isOwn) {
          messageStore.setUnReadMsgCount({ id: newMessage.groupId, count: 1 });
        }
        f7.$(`.newMessageTime${newMessage.receiverId}`).html(instance?.proxy?.$filtersToDate?.msgDate(newMessage.sentAt, 'time'));
      }
    }
  }

  async function sendChatMessage(message_to_send) {
    let msgType = 'text';
    let attachments = [];
    if (documentsUrl.value.length > 0) {
      await authStore.postR2Document({
        shortId: message_to_send.chatGroup.id,
        bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
      });
      attachments = documentsUrl.value;
      msgType = documentsUrl.value[0]?.fileType || 'text';
    }
    message_to_send.attachments = attachments;
    message_to_send.msgType = msgType;
    const isPersonal = message_to_send.groupType === 'PERSONAL' || !group.value || group.value.groupType === 'PERSONAL';
    const endpoint = isPersonal
      ? `/app/message.single.${message_to_send.chatGroup.id}`
      : `/app/message.group.${message_to_send.receiverId}`;
    await messageSocketClient.value.send(endpoint, JSON.stringify(message_to_send));
    await authStore.clearDocumentsUrl();
  }

  // function connectCardService() {
  //   window.localStorage.setItem('scannedCard', '');
  //   messageSocketClient.value.subscribe('/topic/card', (data) => {
  //     setTimeout(() => {
  //       if (data.body.id !== null) f7.$('.buttonIssue').hide();
  //       f7.dialog.alert(data.body);
  //     }, 2000);
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.isCreated', (data) => {
  //     if (data.body.id === null) return;
  //     const cardData = JSON.parse(data.body);
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       subtitle: `Card ${cardData.cardUID} amount: <b>${cardData.currencySymbol}${cardData.amount} ${cardData.currencyCode}</b>`,
  //       text: `Card status: <b>${cardData.status}</b>. Please get the top-up amount from customer before validating.`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.notCreated', (data) => {
  //     if (data.body.id === null) return;
  //     const cardData = JSON.parse(data.body);
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${cardData.status}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.isScanned', (data) => {
  //     if (data.body.cardId === null) return;
  //     const cardData = JSON.parse(data.body);
  //     cardStore.setScannedCard(JSON.parse(data.body));
  //     if (profile.value.sub === cardData.cardIssuerId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `Card ${cardData.cardUID} amount: <b>${cardData.currencySymbol}${cardData.amount} ${cardData.currencyCode}</b>`,
  //         text: `Card status: <b>${cardData.status}</b>. Please get the top-up amount from customer before validating.<br/>Click to validate top-up or cancel it!`,
  //         closeOnClick: true,
  //         on: {
  //           close() {
  //             f7.dialog.create({
  //               text: 'Validate to confirm top-up!',
  //               buttons: [
  //                 { text: 'Validate top-up', color: 'auto', bold: true, onClick() { instance?.proxy?.validateTopUp?.(); } },
  //                 { text: 'Cancel top-up', color: 'red', onClick() { instance?.proxy?.deleteQRCode?.(); } },
  //                 { text: 'Close', close: true },
  //               ],
  //               verticalButtons: true,
  //             }).open();
  //           },
  //         },
  //       }).open();
  //     } else if (profile.value.sub === cardData.recipientUserId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `Card ${cardData.cardUID} amount: <b>${cardData.currencySymbol}${cardData.amount} ${cardData.currencyCode}</b>`,
  //         text: `Card status: <b>${cardData.status}</b>. Please wait validation for your account to be topped-up!`,
  //         closeButton: true,
  //       }).open();
  //     }
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.isLocked', (data) => {
  //     if (data.body.id === null) return;
  //     const cardData = JSON.parse(data.body);
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${cardData.status}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.topUpValidated', (data) => {
  //     if (data.body.cardId === null) return;
  //     const cardData = JSON.parse(data.body);
  //     cardStore.setScannedCard(JSON.parse(data.body));
  //     if (profile.value.sub === cardData.userId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `Card ${cardData.cardUID} amount: <b>${cardData.currencySymbol}${cardData.amount} ${cardData.currencyCode}</b>`,
  //         text: `Card status: <b>${cardData.status}</b>`,
  //         closeButton: true,
  //       }).open();
  //     } else if (profile.value.sub === cardData.recipientId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `Card ${cardData.cardUID} amount: <b>${cardData.currencySymbol}${cardData.amount} ${cardData.currencyCode}</b>`,
  //         text: `Card status: <b>${cardData.status}</b><br/> Go to my account!`,
  //         closeOnClick: true,
  //         on: { close() { goToPrimaryAccount(); } },
  //       }).open();
  //     }
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/card.notActive', (data) => {
  //     if (data.body.id === null) return;
  //     const cardData = JSON.parse(data.body);
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${cardData.status}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  // }

  // async function goToPrimaryAccount() {
  //   f7.preloader.show('multi');
  //   await accountStore.loadPrimaryAccount();
  //   f7.preloader.hide();
  //   await f7router.navigate('/my-transactions/' + accountStore.primaryAccount?.accountNumber + '/primaryTransactionList');
  // }

  // function connectPayService() {
  //   messageSocketClient.value.subscribe('/topic/payment', (data) => {
  //     setTimeout(() => {
  //       if (data.body !== null) f7.$('.buttonIssue').hide();
  //       f7.dialog.alert(data.body);
  //     }, 2000);
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payment.validate', (data) => {
  //     if (data.body === null) return;
  //     const payData = JSON.parse(data.body);
  //     paymentStore.setDecodedQRCode(payData);
  //     f7.dialog.create({
  //       text: `Pay <b>${payData.amount} ${payData.paymentCurrency}</b> to <b>${payData.payee.name}</b>`,
  //       buttons: [
  //         { text: 'Pay', color: 'auto', bold: true, onClick() { instance?.proxy?.payNow?.(); } },
  //         { text: 'Cancel', color: 'red', onClick() { instance?.proxy?.deleteQRCode?.(); } },
  //         { text: 'Close', color: 'gray', close: true },
  //       ],
  //       verticalButtons: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payment.inform', (data) => {
  //     if (data.body === null) return;
  //     const payData = JSON.parse(data.body);
  //     paymentStore.setDecodedQRCode(payData);
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${payData.status}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payment.code.expired', (data) => {
  //     if (data.body === null) return;
  //     f7.toast.create({ text: data.body, cssClass: 'checkCardToast', position: 'center', closeButton: true }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payIsScanned', (data) => {
  //     if (data.body.payId === null) return;
  //     const payData = JSON.parse(data.body);
  //     if (profile.value.sub === payData.payIssuerId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `pay ${payData.payUID} amount: <b>${payData.currencySymbol}${payData.amount} ${payData.currencyCode}</b>`,
  //         text: `pay status: <b>${payData.status}</b>. Please get the top-up amount from customer before validating.<br/>Click to validate top-up or cancel it!`,
  //         closeOnClick: true,
  //         on: {
  //           close() {
  //             f7.dialog.create({
  //               text: 'Validate to confirm top-up!',
  //               buttons: [
  //                 { text: 'Validate top-up', color: 'auto', bold: true, onClick() { instance?.proxy?.validateTopUp?.(); } },
  //                 { text: 'Cancel top-up', color: 'red', onClick() { instance?.proxy?.deleteQRCode?.(); } },
  //                 { text: 'Close', close: true },
  //               ],
  //               verticalButtons: true,
  //             }).open();
  //           },
  //         },
  //       }).open();
  //     } else if (profile.value.sub === payData.recipientUserId) {
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         subtitle: `pay ${payData.payUID} amount: <b>${payData.currencySymbol}${payData.amount} ${payData.currencyCode}</b>`,
  //         text: `pay status: <b>${payData.status}</b>. Please wait validation for your account to be topped-up!`,
  //         closeButton: true,
  //       }).open();
  //     }
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payIsLocked', (data) => {
  //     if (data.body.id !== null) {
  //       const payData = JSON.parse(data.body);
  //       f7.notification.create({
  //         titleRightText: new Date().toLocaleString(),
  //         text: `<b>${payData.status}</b>`,
  //         closeButton: true,
  //       }).open();
  //     }
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/topUpValidated', (data) => {
  //     const body = data.body;
  //     if (!body) return;
  //     const payData = JSON.parse(body);
  //     if (!payData?.payId) return;
  //     const notificationOptions = {
  //       titleRightText: new Date().toLocaleString(),
  //       subtitle: `pay ${payData.payUID} amount: <b>${payData.currencySymbol}${payData.amount} ${payData.currencyCode}</b>`,
  //       closeButton: true,
  //     };
  //     if (profile.value.sub === payData.userId) {
  //       f7.notification.create({ ...notificationOptions, text: `pay status: <b>${payData.status}</b>` }).open();
  //     } else if (profile.value.sub === payData.recipientId) {
  //       f7.notification.create({
  //         ...notificationOptions,
  //         text: `pay status: <b>${payData.status}</b><br/> Go to my account!`,
  //         closeOnClick: true,
  //         on: { close() { goToPrimaryAccount(); } },
  //       }).open();
  //     }
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/payNotActive', (data) => {
  //     const body = data.body;
  //     if (!body) return;
  //     const payData = JSON.parse(body);
  //     if (!payData?.id) return;
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${payData.status}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/vendor.payment.inform', (data) => {
  //     const body = data.body;
  //     if (!body) return;
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${body}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  //   messageSocketClient.value.subscribe('/user/queue/vendor.payment.updateQrCode', (data) => {
  //     const payData = JSON.parse(data.body);
  //     if (!payData?.id) return;
  //     f7.notification.create({
  //       titleRightText: new Date().toLocaleString(),
  //       text: `<b>${payData.amount}</b>`,
  //       closeButton: true,
  //     }).open();
  //   });
  // }

  function notificationsServiceConnect() {
    messageSocketClient.value.subscribe('/user/queue/notification', (data) => {
      const notification = JSON.parse(data.body);
      if (!notification?.id) return;
      notificationStore.addNewNotification(notification);
    });
  }

  function postServiceConnect() {
    const handlePostData = (data) => {
      const post = JSON.parse(data.body);
      if (!post?.id) return;
      postStore.updatePostList(post);
    };
    messageSocketClient.value.subscribe('/user/queue/post/new', handlePostData);
    messageSocketClient.value.subscribe('/post/new', handlePostData);
  }

  function commentServiceConnect() {
    const handleCommentData = (data) => {
      const comment = JSON.parse(data.body);
      if (!comment?.id) return;
      postStore.commentList(comment);
    };
    messageSocketClient.value.subscribe('/user/queue/post/comment/new', handleCommentData);
    messageSocketClient.value.subscribe('/post/comment/new', handleCommentData);
  }

  return {
    socketClient,
    profile, authenticated, csrfToken, documentsUrl,
    chatUser, group, groupList, counterPartUser,
    messageSocketClient, msgCltConnected,
    connectMessageSocket, connectMessageClient, connectMsgdService,
    handleTyping, addReactionToMessage, addGroupMessage, addSubGroupMessage,
    addUserMessage, sendChatMessage, notificationsServiceConnect, postServiceConnect, commentServiceConnect
  };
}
